import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ClipboardText, Package, GitBranch, Trash, CheckCircle, WarningCircle,
  Prohibit, ArrowCounterClockwise, Repeat,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi, PROTO_TONE, DISPOSITION_TONE } from './devApi';
import { EvidenceChips } from './Citations';
import {
  InstallPartsDialog, DispositionDialog, ValidationDialog, RevisionDialog, EvidencePicker,
  CancelPrototypeDialog, CreatePrototypeDialog,
} from './PrototypeDialogs';
import { jstDateTime } from '@/utils/jstDate';

const TH = 'whitespace-nowrap px-2 py-1.5 text-xs font-semibold text-zinc-600';

export default function PrototypeDetail() {
  const { id, pid } = useParams();
  const nav = useNavigate();
  const ctx = useOutletContext() || {};
  const canEdit = !!(ctx.access?.is_admin || ctx.access?.is_member);

  // 処置の取り消し — 完了した案件でも、担当か管理者なら直せる（理由必須・画面内で聞く）。
  // 実機に入ったままの部品を「返却」のままにすると最終原価が 0 円になるため。
  const [undoTarget, setUndoTarget] = useState(null);   // {row, d}
  const [undoReason, setUndoReason] = useState('');
  const [undoBusy, setUndoBusy] = useState(false);

  const submitUndo = async () => {
    const reason = undoReason.trim();
    if (!reason) { toast.error('取り消す理由を入力してください'); return; }
    const { row, d } = undoTarget;
    setUndoBusy(true);
    try {
      await devApi.undoDisposition(id, pid, row.id, d.id, { reason });
      setUndoTarget(null);
      setUndoReason('');
      load();
      ctx.reload?.();
      toast.success('処置を取り消しました（在庫と原価を戻しました）');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail
        || '取り消せませんでした');
    } finally {
      setUndoBusy(false);
    }
  };
  const [data, setData] = useState(null);
  const [planned, setPlanned] = useState([]);
  const [installOpen, setInstallOpen] = useState(false);
  const [valOpen, setValOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [dispRow, setDispRow] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);

  const load = useCallback(() => {
    devApi.prototypes(id).then((d) => {
      setData(d);
      const p = (d.prototypes || []).find((x) => x.id === pid);
      setEvidence(p?.evidence || []);
    }).catch(() => toast.error('試作を読み込めませんでした'));
    devApi.parts(id).then((d) => setPlanned(d.lines || [])).catch(() => {});
  }, [id, pid]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-proto-detail-loading">読み込み中…</div>;
  }
  const p = (data.prototypes || []).find((x) => x.id === pid);
  if (!p) {
    return (
      <EmptyState icon={WarningCircle} title="試作が見つかりません" testid="dev-proto-detail-missing"
        action={<Button onClick={() => nav(`/engineering/developments/${id}`)}>案件に戻る</Button>} />
    );
  }

  const saveEvidence = async (list) => {
    setEvidence(list);
    try { setData(await devApi.patchPrototype(id, pid, { evidence: list })); }
    catch (e) { toast.error(e.response?.data?.detail || '証跡を保存できませんでした'); }
  };
  const uploadPhotos = async (files) => {
    if (files.length === 0) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      try {
        const r = await devApi.uploadDoc(id, f, { title: f.name, doc_type: 'photo',
          link_role: 'photo' });
        added.push({ document_id: r.document.id, title: r.document.title,
          document_number: r.document.document_number, page: null });
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || 'アップロードできませんでした'}`);
      }
    }
    if (added.length > 0) {
      await saveEvidence([...evidence, ...added]);
      setPhotos((x) => [...x, ...added]);
      toast.success(`写真を${added.length}枚追加しました`);
    }
    setUploading(false);
  };
  const uncancel = async () => {
    try {
      setData(await devApi.uncancelPrototype(id, pid));
      toast.success('取消を戻しました');
    } catch (e) { toast.error(e.response?.data?.detail || '戻せませんでした'); }
  };
  const remove = async () => {
    try {
      await devApi.deletePrototype(id, pid);
      toast.success('試作を削除しました');
      nav(`/engineering/developments/${id}`);
    } catch (e) { toast.error(e.response?.data?.detail || '削除できませんでした'); }
  };

  return (
    <div className="space-y-6" data-testid="dev-proto-detail">
      <PageHeader
        title={p.name}
        description={`${p.rebuild_of_name ? `${p.rebuild_of_name} の作り直し · ` : ''}製作日 ${p.build_date || '—'} · 製作台数 ${p.build_quantity} · ${
          (p.builder_names || []).join('、') || '—'}${
          p.drawing_revision_note ? ` · 図面 ${p.drawing_revision_note}` : ''}`}
        testid="dev-proto-detail-header"
        secondary={
          <>
            <Button variant="outline" onClick={() => nav(`/engineering/developments/${id}`)}
              data-testid="dev-proto-back-btn">
              <ArrowLeft size={15} className="mr-1" /> 案件に戻る
            </Button>
            <StatusBadge tone={PROTO_TONE[p.status] || 'neutral'} testid="dev-proto-detail-status">
              {p.status_label}
            </StatusBadge>
          </>
        }
        menuItems={canEdit ? [
          ...(p.cancelled ? [{
            label: '取消を戻す', icon: <ArrowCounterClockwise size={15} />,
            testid: 'dev-proto-uncancel-item', onClick: uncancel,
          }] : [{
            label: '試作を取消す（記録は残ります）', icon: <Prohibit size={15} />,
            testid: 'dev-proto-cancel-item', onClick: () => setCancelOpen(true),
          }]),
          {
            label: '試作を削除する', icon: <Trash size={15} />,
            testid: 'dev-proto-delete-item', onClick: remove,
          },
        ] : undefined}
        primary={
          <Button onClick={() => setValOpen(true)} disabled={!canEdit}
            data-testid="dev-proto-validate-btn">
            <ClipboardText size={15} className="mr-1" /> 検証を記録する
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2" data-testid="dev-proto-secondary-actions">
        <Button variant="outline" size="sm" disabled={!canEdit} onClick={() => setInstallOpen(true)}
          data-testid="dev-proto-install-btn">
          <Package size={14} className="mr-1" /> 部品を投入
        </Button>
        <Button variant="outline" size="sm" disabled={!canEdit} onClick={() => setRevOpen(true)}
          data-testid="dev-proto-create-revision-btn">
          <GitBranch size={14} className="mr-1" /> 改訂を作成
        </Button>
        {(p.cancelled || ['failed', 'needs_revision'].includes(p.status)) && (
          <Button variant="outline" size="sm" disabled={!canEdit} onClick={() => setRebuildOpen(true)}
            data-testid="dev-proto-rebuild-btn">
            <Repeat size={14} className="mr-1" /> この試作から作り直す
          </Button>
        )}
      </div>

      {p.cancelled && (
        <div className="rounded-lg border border-zinc-400 bg-zinc-100 px-4 py-3"
          data-testid="dev-proto-cancelled-banner">
          <p className="text-sm font-bold text-zinc-700">
            この試作は取消されています（{p.cancel_reason_label}）
          </p>
          <p className="mt-0.5 text-sm text-zinc-700">{p.cancel_reason}</p>
          <p className="mt-1 text-xs text-zinc-600">
            {p.cancelled_by_name} · {jstDateTime(p.cancelled_at || '')}
            ／投入した部品・消費した在庫・検証記録はそのまま残っています。未処置の部品は処置の記録が必要です。
          </p>
        </div>
      )}
      {!p.cancelled && (
      <div className={`rounded-lg border px-4 py-3 ${p.readiness.ready
        ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
        data-testid="dev-proto-readiness-box">
        <p className={`flex items-center gap-1.5 text-sm font-bold ${p.readiness.ready
          ? 'text-emerald-900' : 'text-amber-900'}`}>
          {p.readiness.ready ? <CheckCircle size={16} weight="fill" />
            : <WarningCircle size={16} weight="fill" />}
          {p.readiness.message}
        </p>
        {p.readiness.missing_parts.length > 0 && (
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-amber-900">
            {p.readiness.missing_parts.map((m) => (
              <li key={m.part_id} data-testid={`dev-proto-missing-${m.part_id}`}>
                {m.name}: 必要 {m.required}{m.uom} / 投入 {m.installed}{m.uom}（不足 {m.shortage}）
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      <section className="rounded-lg border border-zinc-300 bg-white shadow-sm"
        data-testid="dev-proto-installed">
        <div className="border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <p className="text-sm font-bold text-zinc-800">実際に投入した部品</p>
        </div>
        {p.installed_parts.length === 0 ? (
          <p className="px-4 py-3 text-sm text-zinc-600">まだ部品を投入していません。</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className={TH}>部品</th>
                  <th className={TH}>図番 / 版</th>
                  <th className={TH}>投入</th>
                  <th className={TH}>残り</th>
                  <th className={TH}>処置</th>
                  <th className={TH}>投入者</th>
                  <th className={`${TH} text-right`}>操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {p.installed_parts.map((r) => (
                  <tr key={r.id} data-testid={`dev-proto-part-${r.id}`}>
                    <td className="px-2 py-2 text-sm">
                      <p className="font-semibold text-zinc-900">{r.name}</p>
                      <p className="text-xs text-zinc-600">
                        {r.model_no || ''}{r.inventory_applied ? ' · 在庫反映済み' : ' · 在庫なし（未登録部品）'}
                      </p>
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {r.drawing_no || '—'}
                      {r.drawing_revision ? <span className="ml-1 font-semibold">{r.drawing_revision}</span> : null}
                    </td>
                    <td className="px-2 py-2 text-sm tabular-nums">{r.qty}{r.uom}</td>
                    <td className="px-2 py-2 text-sm tabular-nums">{r.remaining_qty}{r.uom}</td>
                    <td className="px-2 py-2">
                      {(r.disposition_totals || []).length === 0 ? (
                        <span className="text-xs text-zinc-500">未処置</span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {r.disposition_totals.map((d) => (
                            <StatusBadge key={d.type} tone={DISPOSITION_TONE[d.type] || 'neutral'}
                              testid={`dev-proto-disp-${r.id}-${d.type}`}>
                              {d.label} {d.qty}{r.uom}
                            </StatusBadge>
                          ))}
                        </span>
                      )}
                      {(r.dispositions || []).map((d) => (
                        <p key={d.id} className="mt-0.5 text-xs text-zinc-600">
                          {d.type_label} {d.qty}{r.uom}
                          {d.cause_category_label && (
                            <span className="ml-1 font-semibold text-rose-800"
                              data-testid={`dev-proto-disp-cause-${d.id}`}>
                              ［{d.cause_category_label}］
                            </span>
                          )}
                          {' · '}{d.reason} · {d.by_name}
                          {d.undone_at ? (
                            <span className="ml-1 font-semibold text-zinc-500"
                              data-testid={`dev-proto-disp-undone-${d.id}`}>
                              ［取消済み：{d.undo_reason}］
                            </span>
                          ) : (
                            <button type="button" disabled={!canEdit}
                              onClick={() => { setUndoTarget({ row: r, d }); setUndoReason(''); }}
                              data-testid={`dev-proto-disp-undo-${d.id}`}
                              className="ml-1.5 font-semibold text-sky-700 underline hover:text-sky-900 disabled:opacity-40">
                              処分を取り消す
                            </button>
                          )}
                          {d.evidence?.length > 0 && (
                            <span className="ml-1 inline-block align-middle">
                              <EvidenceChips evidence={d.evidence} testid={`dev-proto-disp-ev-${d.id}`} />
                            </span>
                          )}
                        </p>
                      ))}
                    </td>
                    <td className="px-2 py-2 text-xs text-zinc-600">{r.installed_by_name}</td>
                    <td className="px-2 py-2 text-right">
                      {r.remaining_qty > 0 && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          disabled={!canEdit} onClick={() => setDispRow(r)}
                          data-testid={`dev-proto-dispose-btn-${r.id}`}>処置を記録</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-zinc-300 bg-white shadow-sm"
        data-testid="dev-proto-validations">
        <div className="border-b border-zinc-300 bg-zinc-100 px-4 py-2">
          <p className="text-sm font-bold text-zinc-800">検証記録</p>
        </div>
        {p.validations.length === 0 ? (
          <p className="px-4 py-3 text-sm text-zinc-600">検証はまだ記録されていません。</p>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {p.validations.map((v) => (
              <li key={v.id} className="px-4 py-2.5" data-testid={`dev-proto-validation-${v.id}`}>
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <StatusBadge tone={v.result === 'pass' ? 'success'
                    : v.result === 'fail' ? 'danger' : 'warning'}
                    testid={`dev-proto-validation-result-${v.id}`}>{v.result_label}</StatusBadge>
                  <span className="font-semibold text-zinc-900">{v.tested_item}</span>
                  <span className="text-xs text-zinc-600">
                    {v.tested_at} · {v.tester_name}
                    {v.part_name ? ` · 対象 ${v.part_name}` : ''}
                    {v.drawing_revision ? `（${v.drawing_revision}）` : ''}
                  </span>
                </p>
                {v.issue_description && (
                  <p className="mt-0.5 text-sm text-zinc-800">不具合: {v.issue_description}</p>
                )}
                {(v.failure_category_label || v.cause || v.countermeasure) && (
                  <div className="mt-1 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1 text-xs text-zinc-800"
                    data-testid={`dev-proto-failure-${v.id}`}>
                    <p className="flex flex-wrap gap-2">
                      {v.failure_category_label && (
                        <span className="font-semibold">分類: {v.failure_category_label}</span>
                      )}
                      {v.failure_location && <span>発生箇所: {v.failure_location}</span>}
                      {v.recurrence && (
                        <span className="font-semibold text-rose-700">再発</span>
                      )}
                    </p>
                    {v.cause && <p>原因: {v.cause}</p>}
                    {v.countermeasure && <p>対策: {v.countermeasure}</p>}
                  </div>
                )}
                <div className="mt-1">
                  <EvidenceChips evidence={v.evidence} testid={`dev-proto-validation-ev-${v.id}`} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm"
        data-testid="dev-proto-evidence">
        <p className="mb-2 text-sm font-bold text-zinc-800">試作の写真・証跡</p>
        <div className="mb-3 rounded-md border border-dashed border-zinc-400 bg-zinc-50 p-3"
          data-testid="dev-proto-photo-upload">
          <p className="text-xs font-semibold text-zinc-700">
            写真をこの試作に追加（ドキュメントアプリに保存され、この試作に紐づきます）
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input type="file" accept="image/*" multiple disabled={!canEdit || uploading}
              onChange={(e) => uploadPhotos(Array.from(e.target.files || []))}
              className="text-sm" data-testid="dev-proto-photo-input" />
            {uploading && <span className="text-xs font-semibold text-teal-800">アップロード中…</span>}
          </div>
          {photos.length > 0 && (
            <div className="mt-2" data-testid="dev-proto-photo-list">
              <EvidenceChips evidence={photos} testid="dev-proto-photo-chips" />
            </div>
          )}
        </div>
        <div className="mb-3">
          <p className="mb-1 text-xs font-semibold text-zinc-700">
            この試作に紐づく証跡（クリックで書類を開きます）
          </p>
          <EvidenceChips evidence={evidence} testid="dev-proto-evidence-chips" />
        </div>
        <EvidencePicker picked={evidence} onChange={saveEvidence} testid="dev-proto-evidence-picker" />
      </section>

      <InstallPartsDialog devId={id} proto={p} open={installOpen} onOpenChange={setInstallOpen}
        onDone={(d) => { setData(d); load(); }} />
      <ValidationDialog devId={id} proto={p} open={valOpen} onOpenChange={setValOpen}
        results={data.results} categories={data.failure_categories} parts={planned}
        onDone={(d) => { setData(d); load(); }} />
      <RevisionDialog devId={id} open={revOpen} onOpenChange={setRevOpen} parts={planned}
        onDone={() => load()} />
      <CancelPrototypeDialog devId={id} proto={p} reasons={data.cancel_reasons}
        open={cancelOpen} onOpenChange={setCancelOpen} onDone={(d) => { setData(d); load(); }} />
      <CreatePrototypeDialog devId={id} open={rebuildOpen} onOpenChange={setRebuildOpen}
        rebuildOf={p} onDone={() => nav(`/engineering/developments/${id}`)} />
      {dispRow && (
        <DispositionDialog devId={id} proto={p} row={dispRow} types={data.dispositions}
          categories={data.failure_categories}
          onClose={() => setDispRow(null)} onDone={(d) => { setData(d); load(); }} />
      )}

      <Dialog open={!!undoTarget} onOpenChange={(o) => { if (!o) setUndoTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="dev-proto-undo-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">処分を取り消す</DialogTitle>
          </DialogHeader>
          {undoTarget && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-600">
                「{undoTarget.d.type_label} {undoTarget.d.qty}{undoTarget.row.uom || ''}」を取り消して
                試作に搭載へ戻します。在庫と原価も戻ります。
              </p>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">
                  理由 <span className="text-rose-500">＊</span>
                </Label>
                <Textarea rows={3} value={undoReason} className="mt-1 text-sm"
                  placeholder="例: 実機に入ったままなので返却の記録が誤り"
                  onChange={(e) => setUndoReason(e.target.value)}
                  data-testid="dev-proto-undo-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)}
              data-testid="dev-proto-undo-cancel">やめる</Button>
            <Button onClick={submitUndo} disabled={undoBusy || !undoReason.trim()}
              data-testid="dev-proto-undo-submit">取り消す</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
